import { useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { UseDosiminMutationOptions, UseDosiminQueryOptions } from '~/components/Query/Query.hooks';

export type Event = {
  id: number;
  isJoinable: boolean;
  type: EventType;
  reward: {
    assetType: string;
    amount: number;
  };
};

export type EventType = 'CHECK_IN' | 'REFERRAL' | 'PURCHASE_TRANSACTION' | 'BONUS_CHECK_IN';

export const eventsUrl = '/api/citizen/v1/events';

export type MissionBuff = {
  amount: number;
  missionList: {
    id: number;
    plusAmount: number;
  }[];
};

export type CheckInReward = {
  level: number;
  holdingNums: number;
  amount: number;
};

export type CheckInResponse = {
  success: boolean;
  maxLevel: number;
  totalAmount: number;
  reward: CheckInReward[];
  missionBuff: MissionBuff;
};

export const getEvents = async () => {
  const {
    data: { eventList },
  } = await axios.get<{ eventList: Event[] }>(eventsUrl);
  return eventList;
};

export const useEvents = <E = unknown, D = Event[]>(
  options?: UseDosiminQueryOptions<Event[], E, D>
) => useQuery<Event[], E, D>(['events'], getEvents, options);

export const useEvent = (
  type: EventType,
  options?: UseDosiminQueryOptions<Event[], unknown, Event | undefined>
) =>
  useEvents({
    ...options,
    select: (events) => events.find((event) => event.type === type),
  });

export const checkInUrl = '/api/citizen/v1/events/check-in';
export const checkIn = async () => {
  const { data } = await axios.post<CheckInResponse>(checkInUrl);
  return data;
};

export const useCheckIn = (options?: UseDosiminMutationOptions<CheckInResponse>) =>
  useMutation<CheckInResponse>(checkIn, options);

export type BonusDonReward = {
  success: boolean;
  reward: {
    assetType: 'DON';
    amount: number;
  };
};

export const bonusDonUrl = '/citizen/v1/events/check-in/bonus';

export const claimBonusDon = async () => {
  const {
    data: { reward },
  } = await axios.post<{ reward: BonusDonReward }>(bonusDonUrl);
  return reward;
};

export const useClaimBonusDon = (options?: UseMutationOptions<BonusDonReward>) =>
  useMutation<BonusDonReward>({
    mutationFn: claimBonusDon,
    ...options,
  });
